/**
 * `/admin` and `/admin/<section>`.
 *
 * The same shape `news-route.ts` uses, and for the same stated reason: this is
 * a pure path parser plus a `navigate()` that pushes history, not a router. The
 * argument in `deep-link.ts` still holds — a history stack around a WebGL canvas
 * is not warranted — and the admin panel is the second place where a real,
 * refreshable URL genuinely earns its keep. An operator sends a colleague a link
 * to the players list; a hard refresh on `/admin/players` has to land there.
 *
 * Both the Vite dev server and the production static server already fall back to
 * `index.html` for unknown paths, so nothing on that side had to change. **What
 * did have to change is Caddy**: `/admin/*` used to be reverse-proxied to the
 * Kottster container, so it never reached the SPA at all. That route is gone
 * along with Kottster.
 */

/** The sections, in the order the panel's rail lists them. */
export const ADMIN_SECTIONS = [
  'overview',
  'players',
  'content',
  'invoices',
  'settings',
  'payment-methods',
  'withdrawals',
  'meta',
  'keys',
] as const;

export type AdminSection = (typeof ADMIN_SECTIONS)[number];

export const ADMIN_SECTION_LABELS: Readonly<Record<AdminSection, string>> = {
  overview: 'Overview',
  players: 'Players',
  content: 'Content',
  invoices: 'Invoices',
  settings: 'Settings',
  'payment-methods': 'Payment methods',
  withdrawals: 'Withdrawals',
  meta: 'Meta tags',
  keys: 'API keys',
};

/**
 * `null` when the path is not the admin panel at all.
 *
 * An unknown section falls back to `overview` rather than to `null`, the same
 * "absent is not broken" call `parseNewsRoute` makes for a slug-less article
 * link: somebody who mistypes a section should land in the panel, not be thrown
 * back to the game with no explanation.
 */
export function parseAdminRoute(pathname: string): AdminSection | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/admin') return 'overview';

  const match = /^\/admin\/([^/]+)$/.exec(path);
  if (!match) return null;

  const section = decodeURIComponent(match[1]).trim().toLowerCase();
  return (ADMIN_SECTIONS as readonly string[]).includes(section)
    ? (section as AdminSection)
    : 'overview';
}

/** Reads the live URL. The one impure line, kept out of the parser. */
export function readAdminRoute(): AdminSection | null {
  if (typeof window === 'undefined') return null;
  return parseAdminRoute(window.location.pathname);
}

/**
 * Moves to a section.
 *
 * Dispatches `popstate` itself so a programmatic navigation is indistinguishable
 * from the browser's own back button — exactly what `navigateNews` does, and
 * what lets `App.vue` hold one listener rather than one per caller.
 */
export function navigateAdmin(section: AdminSection): void {
  const path = section === 'overview' ? '/admin' : `/admin/${section}`;
  if (window.location.pathname !== path) {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

/** Back to the game, address bar and all. */
export function leaveAdmin(): void {
  window.history.pushState({}, '', '/');
  window.dispatchEvent(new PopStateEvent('popstate'));
}
